import { VRM, VRMUtils } from '@pixiv/three-vrm'
import { useFrame } from '@react-three/fiber'
import { type MutableRefObject, useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import type { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { reportAvatarFailure } from '@/shared/lib/avatarDiagnostics'
import { retainManagedObjectUrl } from '@/shared/lib/managedObjectUrl'
import { applyIosTextureResize } from '../lib/applyIosTextureResize'
import { disposeVrmScene } from '../lib/disposeVrmScene'

type UseVrmModelParams = {
  avatarFile: string
  gltfLoader: GLTFLoader
  onBeforeLoad?: () => void
  onVrmLoaded?: (vrm: VRM) => void
  onLoadResult?: (url: string, success: boolean) => void
}

type UseVrmModelResult = {
  vrm: VRM | undefined
  mixerRef: MutableRefObject<THREE.AnimationMixer | null>
}

/**
 * avatarFile から VRM を読み込み、VRMA 再生に使う AnimationMixer と一緒に管理する。
 *
 * この hook は VRMModel から three.js のモデル管理を切り離すための処理で、主に以下を担当する。
 *
 * - GLTFLoader に VRMLoaderPlugin を登録して VRM を読み込む
 * - VRM0 の向きを VRMUtils.rotateVRM0 で補正する
 * - 読み込んだ VRM の scene を対象に AnimationMixer を作成する
 * - useFrame で AnimationMixer と VRM runtime を毎フレーム更新する
 * - avatarFile 変更時や unmount 時に再生中 action を停止して mixer を破棄する
 */
export const useVrmModel = ({
  avatarFile,
  gltfLoader,
  onBeforeLoad,
  onVrmLoaded,
  onLoadResult,
}: UseVrmModelParams): UseVrmModelResult => {
  const [vrm, setVrm] = useState<VRM>()
  const mixerRef = useRef<THREE.AnimationMixer | null>(null)
  const liveVrmRef = useRef<VRM | undefined>(undefined)

  useFrame((_, delta) => {
    mixerRef.current?.update(delta)
    liveVrmRef.current?.update(delta)
  })

  useEffect(() => {
    let canceled = false
    let ownedVrm: VRM | undefined
    let ownedMixer: THREE.AnimationMixer | undefined
    const releaseUrl = retainManagedObjectUrl(avatarFile)
    setVrm(undefined)
    const fail = () => {
      if (canceled) return
      reportAvatarFailure('vrm_parse', undefined, 'AVATAR_LOAD_FAILED')
      onLoadResult?.(avatarFile, false)
    }
    try {
      onBeforeLoad?.()
      gltfLoader.load(
        avatarFile,
        (gltf) => {
          const loadedVrm: unknown = gltf.userData.vrm
          if (canceled || !(loadedVrm instanceof VRM)) {
            disposeVrmScene(gltf.scene)
            releaseUrl()
            if (!canceled) fail()
            return
          }
          try {
            // VRM0 は標準の正面方向が VRM1 と異なるため、読み込み直後に向きを揃える。
            if (loadedVrm.meta?.metaVersion === '0') VRMUtils.rotateVRM0(loadedVrm)

            loadedVrm.scene.traverse((object: THREE.Object3D) => {
              // カメラが寄っても顔を非表示にしない設定
              object.frustumCulled = false
            })
            // VRM を表示する前に texture.image を差し替えることで、iOS Safari の初回 GPU メモリピークを下げる。
            applyIosTextureResize(loadedVrm.scene)

            onVrmLoaded?.(loadedVrm)
            ownedVrm = loadedVrm
            ownedMixer = new THREE.AnimationMixer(loadedVrm.scene)
            mixerRef.current = ownedMixer
            liveVrmRef.current = loadedVrm
            setVrm(loadedVrm)
            onLoadResult?.(avatarFile, true)
          } catch {
            ownedMixer?.stopAllAction()
            ownedMixer?.uncacheRoot(loadedVrm.scene)
            ownedVrm = undefined
            ownedMixer = undefined
            mixerRef.current = null
            liveVrmRef.current = undefined
            setVrm(undefined)
            disposeVrmScene(loadedVrm.scene)
            fail()
          } finally {
            releaseUrl()
          }
        },
        undefined,
        () => {
          releaseUrl()
          fail()
        }
      )
    } catch {
      releaseUrl()
      fail()
    }

    return () => {
      canceled = true
      liveVrmRef.current = undefined
      mixerRef.current = null
      ownedMixer?.stopAllAction()
      if (ownedVrm) {
        ownedMixer?.uncacheRoot(ownedVrm.scene)
        disposeVrmScene(ownedVrm.scene)
      }
    }
  }, [avatarFile, gltfLoader, onBeforeLoad, onVrmLoaded, onLoadResult])

  return { vrm: vrm === liveVrmRef.current ? vrm : undefined, mixerRef }
}
